/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import I18nContextComponent from '@oneaudi/i18n-context';
import { getDesignLibrary } from '../alpha-helper';
import { i18nScope } from './i18n/messages';
import type {
  GfaLocaleService,
  GfaServiceConfigProvider,
  I18NService,
  AsyncSsrManager,
  NotificationDisplayService,
} from './feature-service-types';
import type { ContentType, StaticConfig, StaticFalconConfig } from './types';
import { Notifications } from './notifications';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 'notification-display-service': NotificationDisplayService | undefined;
  readonly 'gfa:service-config-provider': GfaServiceConfigProvider;
  readonly 'gfa:locale-service': GfaLocaleService;
  readonly 's2:logger'?: Logger;
  readonly 'dbad:audi-i18n-service': I18NService;
  readonly 's2:async-ssr-manager': AsyncSsrManager | undefined;
}

export interface NotificationsProps {
  notificationService?: NotificationDisplayService;
  contentType?: ContentType;
}

export function normalizeConfig(
  config?: StaticConfig | StaticFalconConfig,
): StaticConfig | undefined {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (config.__type === 'aem-headless') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return config?.config?.fields as StaticConfig;
  }
  return config as StaticConfig;
}

const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  FeatureServiceDependencies,
  StaticConfig
> = {
  dependencies: {
    featureServices: {
      'notification-display-service': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
    },
    externals: {
      ...getDesignLibrary({
        unifiedWebCommonVersion: '^1.8.0',
        unifiedWebAlphaCommonVersion: '^1.9.0',
      }),
      react: '^18.2.0',
      'styled-components': '^5.1.1',
    },
  },
  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
    },
  },

  create: ({ config: rawConfig, featureServices }) => {
    const config = normalizeConfig(rawConfig);

    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');

    const notificationService = featureServices['notification-display-service'];
    if (!notificationService) {
      throw new Error(`Invalid Notification Service`);
    }
    const i18nData: I18NService = featureServices['dbad:audi-i18n-service'];

    return {
      render: () => {
        return (
          <ThemeProvider>
            <I18nContextComponent i18nData={i18nData} scopes={[i18nScope]}>
              <Notifications
                contentType={config?.contentType}
                notificationService={notificationService}
              />
            </I18nContextComponent>
          </ThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
